<template>
  <div class="share-landing">
    <transition name="share-loading-fade">
      <page-loading v-if="showInitialLoading" mode="standalone" text="正在打开分享" />
    </transition>
    <header class="top">
      <img src="@/assert/xyo-chat-logo.png" alt="xyo.chat" class="logo" />
      <strong>xyo.chat</strong>
      <span>AI companion for every day</span>
    </header>

    <div class="card">
      <p class="from">来自 <b>{{ share.fromUser }}</b> 的分享</p>
      <h1>{{ share.title }}</h1>
      <p class="message">{{ share.message }}</p>

      <div class="role">
        <img :src="require('@/assert/主页人物.png')" alt="" class="cover" />
        <div class="role-info">
          <h2>{{ share.roleName }}</h2>
          <p>{{ share.roleDesc }}</p>
          <div class="tags">
            <span v-for="t in share.tags" :key="t">{{ t }}</span>
          </div>
        </div>
      </div>

      <div class="actions">
        <el-button type="primary" round class="import-btn" :loading="importing" @click="doImport">
          添加到我的角色卡
        </el-button>
        <el-button round @click="$router.push('/role-cards')">先看看其他角色</el-button>
      </div>
      <p class="tip"><i class="el-icon-lock"></i> 导入会复制一份独立角色卡到你的账号，原作者后续修改不影响你</p>
    </div>

    <!-- 导入成功后授权弹窗 -->
    <el-dialog title="导入成功，是否授权对方？" :visible.sync="grantVisible" width="420px" custom-class="yw-dialog" :show-close="false">
      <div class="grant-body">
        <p class="ok"><i class="el-icon-circle-check"></i> 「{{ share.roleName }}」已添加到你的角色卡</p>
        <p class="ask">你可以授权分享者 <b>{{ share.fromUser }}</b> 查看用这张卡生成伴侣的对话，甚至代替伴侣回复你。授权可随时撤销。</p>
        <div class="grant-opt">
          <div class="opt">
            <div>
              <b>允许查看对话</b>
              <small>对方可看到该伴侣的聊天记录</small>
            </div>
            <el-switch v-model="grant.canView" active-color="#ff4d8f" />
          </div>
          <div class="opt">
            <div>
              <b>允许代聊回复</b>
              <small>对方可代替伴侣发送消息（经微信网关转发）</small>
            </div>
            <el-switch v-model="grant.canTakeover" active-color="#ff4d8f" :disabled="!grant.canView" />
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button round :disabled="granting" @click="finish(false)">暂不授权</el-button>
        <el-button type="primary" round :loading="granting" @click="finish(true)">确认授权</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { shareApi, roleCardApi, getToken } from "@/services/api";
import PageLoading from "@/components/PageLoading.vue";

export default {
  name: "ShareLandingView",
  components: { PageLoading },
  data() {
    return {
      importing: false,
      granting: false,
      initialLoading: true,
      showInitialLoading: false,
      grantVisible: false,
      canImport: true,
      importResult: null,
      share: {
        title: "正在加载分享…",
        message: "",
        roleName: "",
        roleDesc: "",
        fromUser: "对方",
        tags: [],
      },
      grant: { canView: true, canTakeover: false },
    };
  },
  created() {
    this._initialLoadingStartedAt = Date.now();
    this.showInitialLoading = true;
    this.loadShare();
  },
  beforeDestroy() {
    clearTimeout(this._initialLoadingTimer);
  },
  methods: {
    async loadShare() {
      const token = this.$route.params.token;
      try {
        const v = await shareApi.publicView(token);
        this.share = {
          title: v.title,
          message: v.message,
          roleName: v.role_card_name,
          roleDesc: v.role_card_description,
          fromUser: v.owner_display_name || "对方",
          tags: [],
        };
        this.canImport = v.can_import;
      } catch (e) {
        this.share.title = "分享不存在或已失效";
        this.share.message = e.message || "";
        this.canImport = false;
      } finally {
        clearTimeout(this._initialLoadingTimer);
        this.initialLoading = false;
        const elapsed = Date.now() - this._initialLoadingStartedAt;
        this._initialLoadingTimer = setTimeout(() => {
          this.showInitialLoading = false;
        }, Math.max(0, 420 - elapsed));
      }
    },
    async doImport() {
      if (!getToken()) {
        this.$message.warning("请先登录后再导入");
        this.$router.push({ name: "login" });
        return;
      }
      this.importing = true;
      try {
        this.importResult = await shareApi.import(this.$route.params.token);
        this.grantVisible = true;
      } catch (e) {
        this.$message.error(e.message || "导入失败");
      } finally {
        this.importing = false;
      }
    },
    async finish(granted) {
      const wantGrant = granted && (this.grant.canView || this.grant.canTakeover);
      if (!wantGrant) {
        this.grantVisible = false;
        this.$message.success("已导入，未授权对方");
        this.$router.push("/role-cards");
        return;
      }
      // 授权挂在伴侣上，先用导入的角色卡创建伴侣再授权
      this.granting = true;
      try {
        const card = this.importResult.imported_role_card;
        const companion = await roleCardApi.createCompanion(card.id, null);
        await shareApi.grant(companion.id, {
          share_id: this.importResult.share_id,
          can_view_messages: this.grant.canView,
          can_takeover_reply: this.grant.canTakeover,
        });
        this.grantVisible = false;
        this.$message.success(`已创建伴侣「${companion.name}」并授权对方`);
        this.$router.push({ path: "/companion-detail", query: { id: companion.id } });
      } catch (e) {
        this.$message.error(e.message || "授权失败，可稍后在个人中心重试");
      } finally {
        this.granting = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.share-landing {
  position: relative;
  min-height: 100vh;
  padding: 28px 20px 60px;
  box-sizing: border-box;
  background:
    radial-gradient(circle at 80% 12%, rgba(255, 200, 222, 0.5), transparent 35%),
    linear-gradient(160deg, #fff6fa 0%, #fdeaf2 60%, #fbe4ee 100%);

  .top {
    display: flex;
    align-items: center;
    max-width: 520px;
    margin: 0 auto 28px;

    .logo {
      width: 40px;
      height: 40px;
      margin-right: 10px;
      object-fit: contain;
      border-radius: 10px;
    }

    strong {
      margin-right: 10px;
      font-size: 22px;
      font-weight: 700;
      color: #1f1f1f;
    }

    span {
      color: #a08a93;
      font-size: 12px;
    }
  }

  .card {
    max-width: 520px;
    margin: 0 auto;
    padding: 32px 32px 26px;
    border-radius: 28px;
    background: rgba(255, 255, 255, 0.94);
    box-shadow: 0 20px 60px rgba(255, 100, 150, 0.14);

    .from {
      margin: 0 0 10px;
      color: #999;
      font-size: 13px;

      b {
        color: #ff4d8f;
      }
    }

    h1 {
      margin: 0 0 10px;
      font-size: 26px;
      font-weight: 700;
      color: #2b2b2b;
      line-height: 1.4;
    }

    .message {
      margin: 0 0 22px;
      color: #888;
      font-size: 14px;
      line-height: 1.7;
    }

    .role {
      display: flex;
      gap: 16px;
      margin-bottom: 24px;
      padding: 16px;
      border-radius: 18px;
      background: linear-gradient(135deg, #fff5fa, #fff);

      .cover {
        width: 96px;
        height: 120px;
        flex-shrink: 0;
        border-radius: 14px;
        object-fit: cover;
        object-position: center top;
        background: #fce4ef;
      }

      .role-info {
        flex: 1;

        h2 {
          margin: 0 0 8px;
          font-size: 20px;
        }

        p {
          margin: 0 0 10px;
          color: #999;
          font-size: 13px;
          line-height: 1.6;
        }

        .tags {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;

          span {
            padding: 2px 10px;
            border-radius: 10px;
            color: #ff7eb0;
            font-size: 11px;
            background: #fff0f6;
          }
        }
      }
    }

    .actions {
      display: flex;
      gap: 10px;

      .import-btn {
        flex: 1;
        height: 46px;
        border: 0;
        font-size: 15px;
        background: linear-gradient(90deg, #ff6b9d, #ff3d86);
        box-shadow: 0 10px 24px rgba(255, 77, 143, 0.3);
      }
    }

    .tip {
      margin: 16px 0 0;
      color: #b6a9af;
      font-size: 12px;
      text-align: center;

      i {
        margin-right: 4px;
      }
    }
  }
}

.grant-body {
  .ok {
    margin: 0 0 14px;
    color: #52c41a;
    font-size: 14px;

    i {
      margin-right: 4px;
    }
  }

  .ask {
    margin: 0 0 16px;
    color: #777;
    font-size: 13px;
    line-height: 1.7;

    b {
      color: #ff4d8f;
    }
  }

  .grant-opt {
    .opt {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 0;
      border-top: 1px solid #f5f0f2;

      b {
        display: block;
        font-size: 13px;
      }

      small {
        color: #aaa;
        font-size: 12px;
      }
    }
  }
}

.share-loading-fade-enter-active,
.share-loading-fade-leave-active {
  transition: opacity 180ms ease;
}

.share-loading-fade-enter,
.share-loading-fade-leave-to {
  opacity: 0;
}
</style>
